// @flow

import React, { Fragment } from 'react';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import { I18n, Translate } from 'react-redux-i18n';
import type { MapViewer } from '../../../../types/VisioglobeType';
import GeminiRightTab from './GeminiRightTab';
import SurfaceConvertor from '../../../common/SurfaceConvertor';
import type { BranchLevels } from '../../../../types/shoppingCenterType';
import { isKPIValuesReversed } from '../../../../utils/utils';
import { getCurrentShoppingCenterFromStore, getCurrentStore } from '../../../../utils/urlUtils';
import type {
  BranchLevelGemini,
  BusinessPlanBean,
  KPIs,
  ShoppingCenterGemini,
  StoreGemini,
  VisioglobeUnitsMap,
  YearMonth,
} from '../../../../types/geminiType';
import { VisioglobeEmptyUnit } from '../../../../types/geminiType';
import {
  all,
  branch,
  branchLevelColor,
  geminiColors,
  OCR_QUANTIL_COLOR,
  OCR_QUANTIL_VALUE,
  reconciliationStates,
  shop,
  shoppingCenter,
  visioGlobeDefaultColor,
  visioGlobeEmptyUnitColor,
  visioGlobeSelectedColor,
  visioGlobeUnselectedColor,
  zone,
} from '../../../../const';
import CurrencySurfaceConvertor from '../../../common/CurrencySurfaceConvertor';
import {
  computeColorZone,
  computeOcupancyCost,
  getAllDisplayableUnits,
  getCurrentQuartil,
  getOkUnits,
  glaDivider,
  initMap,
  sumKpis,
} from './geminiFunctions';
import {
  erv,
  glaWithoutSst,
  glaWithSst,
  invSbr,
  occupancyCost,
  ocr,
  revPot,
  salesNum,
  salesR12m,
  salesR12mWithoutSst,
  spotMgr,
} from './geminiConst';
import GeminiLeftTab from './GeminiLeftTab';
import GeminiLegend from './GeminiLegend';
import ComparablesModalContent from './comparables/ComparablesModalContent';
import BusinessPlanTimeline from './BusinessPlanTimeline';
import IconButton from '../../../../components/IconButton';
import VisioGlobeUI from '../../../common/VisioGlobeUI';
import QuantilsLegend from './QuantilsLegend';
import ModalWrapper from '../../../../components/ModalWrapper';
import ChartModal from './ChartModal';
import { trackAction } from '../../../../components/GoogleAnalyticsWrapper';

type Props = {
  dispatch: () => void,
  history: History,
  mapviewer: MapViewer,
  branchLevels: BranchLevels[],
  updateVisioglobeUnits: Function,
  shoppingCenterData: ShoppingCenterGemini,
  branchKpi: BranchLevelGemini[],
  branchKpiAvg: BranchLevelGemini[],
  isCurrencyBySurface: boolean,
  scrollableElement: HTMLDivElement,
  changeMappingMode: Function,
  isMappingUser: boolean,
  isGeminiAdmin: boolean,
  isPulseAuthorized: boolean,
  visioglobeUnitsMap: VisioglobeUnitsMap,
}

type State = {
  selectedCategory: string,
  selectedKpi: ?KPIs,
  selectedStore: ?StoreGemini,
  storeCategory: string,
  currentKpiFilter: string,
  selectedBranches: number[],
  selectedZone: string[],
  categoryOverride: boolean,
  leftMenuHidden: boolean,
  rightMenuHidden: boolean,
  yearMonth: YearMonth,
  year: number,
  showComparable: boolean,
  showChart: boolean,
  selectedBusinessPlan: ?BusinessPlanBean,
  displayBusinessPlanModule: boolean,
  businessPlanStoreAttributes: { codstr: number, color: string }[],
  isDisplayEmptyUnits: boolean,
  isFilters: boolean,
  isUp: boolean,
  legendHidden: boolean,
}

const KPI_SELECT = 'KPI_SELECT';
const STORE_SELECT = 'STORE_SELECT';
const STORE_CHART = 'STORE_CHART';
const STORE_COMPARABLE = 'STORE_COMPARABLE';
const SELECT_POV = 'SELECT_POV';
const SELECT_US = 'SELECT_US';

const kpiByGla = [salesR12m, erv, spotMgr, invSbr, revPot, occupancyCost];

class Overlay extends React.Component<Props, State> {
  state: State = {
    selectedCategory: shoppingCenter,
    selectedStore: null,
    selectedZone: [],
    selectedKpi: null,
    currentKpiFilter: ocr,
    selectedBranches: [],
    storeCategory: all,
    categoryOverride: false,
    leftMenuHidden: false,
    rightMenuHidden: false,
    yearMonth: null,
    year: 0,
    showComparable: false,
    showChart: false,
    selectedBusinessPlan: null,
    displayBusinessPlanModule: false,
    businessPlanStoreAttributes: [],
    isDisplayEmptyUnits: false,
    isFilters: true,
    isUp: false,
    legendHidden: true,
  };

  shoppingCenter = getCurrentShoppingCenterFromStore();

  zoneColorMap = {};

  setInitialKpi = (initialKpi) => {
    this.setState({
      yearMonth: { year: initialKpi.year, month: initialKpi.month },
      selectedKpi: initialKpi,
      year: initialKpi.year,
    });
  };

  componentDidMount() {
    this.props.mapviewer.setSelectStoreCallback(this.selectStoreCallback);

    const displayableUnits = getAllDisplayableUnits(this.props.visioglobeUnitsMap);
    this.zoneColorMap = computeColorZone(displayableUnits);
    const initialKpi = this.props.shoppingCenterData.kpi.sort((a, b) => b.year - a.year)[0];
    if (!initialKpi) {
      return;
    }
    this.setInitialKpi(initialKpi);

    initMap(
      displayableUnits,
      this.props.mapviewer,
    );

    this.updateMapColor(
      this.props.visioglobeUnitsMap,
      this.props.shoppingCenterData,
      this.state.currentKpiFilter,
      this.state.storeCategory,
      this.state.selectedCategory,
      this.state.selectedBranches,
      this.state.selectedZone,
      this.props.isCurrencyBySurface,
    );

    const initialStore = displayableUnits.find(store => getCurrentStore()
      && store.codstr && store.codstr.toString() === getCurrentStore());

    if (initialStore) {
      this.selectStoreCallback(initialStore);
      this.props.mapviewer
        .selectPlace(initialStore.idVisioglobes, () => this.forceUpdate());
    }
  }

  shouldComponentUpdate(nextProps: Props, nextState: State, nextContext: any): boolean {
    if (this.props.isCurrencyBySurface !== nextProps.isCurrencyBySurface) {
      this.updateMapColor(
        nextProps.visioglobeUnitsMap,
        nextProps.shoppingCenterData,
        nextState.currentKpiFilter,
        nextState.storeCategory,
        nextState.selectedCategory,
        nextState.selectedBranches,
        nextState.selectedZone,
        nextProps.isCurrencyBySurface,
      );
    }
    return true;
  }

  selectStoreCallback = (selectedStore: StoreGemini) => {
    if (!selectedStore) {
      return;
    }
    if (this.state.displayBusinessPlanModule) {
      this.setState({
        selectedStore,
        selectedBusinessPlan: null,
        selectedCategory: shop,
      });
      return;
    }
    if (this.state.isDisplayEmptyUnits) {
      this.setState({
        selectedStore,
        selectedCategory: shop,
      });
      return;
    }
    trackAction(STORE_SELECT);
    if (this.state.selectedCategory === branch) {
      if (this.state.selectedBranches.filter(selectedBranch =>
        selectedBranch === selectedStore.bl1Id).length > 0
      ) {
        this.setState({
          selectedKpi: selectedStore.kpi,
          categoryOverride: true,
          selectedStore,
        });
      } else {
        this.updateMapColor(
          this.props.visioglobeUnitsMap,
          this.props.shoppingCenterData,
          this.state.currentKpiFilter,
          this.state.storeCategory,
          branch,
          [selectedStore.bl1Id],
          this.state.selectedZone,
          this.props.isCurrencyBySurface,
        );
        this.setState({
          selectedBranches: [selectedStore.bl1Id],
          selectedStore: null,
          categoryOverride: false,
          selectedKpi: (
            this.props.branchKpi
              .filter(branchLevel => branchLevel.blId === selectedStore.bl1Id)
              .sort((a, b) => b.year - a.year)[0]: KPIs
          ),
        });
      }
    } else if (this.state.selectedCategory === zone) {
      if (this.state.selectedZone
        && this.state.selectedZone.filter(selectedZone =>
          selectedZone === selectedStore.zoneName).length > 0
      ) {
        this.setState({
          selectedKpi: selectedStore.kpi,
          categoryOverride: true,
          selectedStore,
        });
      } else {
        this.updateMapColor(
          this.props.visioglobeUnitsMap,
          this.props.shoppingCenterData,
          this.state.currentKpiFilter,
          this.state.storeCategory,
          zone,
          this.state.selectedBranches,
          [selectedStore.zoneName],
          this.props.isCurrencyBySurface,
        );
        this.setState({
          selectedZone: [selectedStore.zoneName],
          selectedStore: null,
          categoryOverride: false,
          selectedKpi: this.sumZoneKpis([selectedStore.zoneName]),
        });
      }
    } else {
      this.setState({
        selectedCategory: shop,
        selectedKpi: selectedStore.kpi,
        selectedStore,
      });
      this.updateMapColor(
        this.props.visioglobeUnitsMap,
        this.props.shoppingCenterData,
        this.state.currentKpiFilter,
        this.state.storeCategory,
        shop,
        this.state.selectedBranches,
        this.state.selectedZone,
        this.props.isCurrencyBySurface,
      );
    }
  };

  updateMapColor = (
    visioglobeUnitsMap: VisioglobeUnitsMap,
    shoppingCenterData: ShoppingCenterGemini,
    kpiSelectedParam: string,
    storeCategory: string,
    selectedCategory: string,
    selectedBranch: number[],
    selectedZone: string[],
    isCurrencyBySurface: boolean,
  ) => {
    if (this.state.isDisplayEmptyUnits) {
      this.updateColorsForEmptyUnits();
      return;
    }
    if (this.state.displayBusinessPlanModule) {
      this.updateColorsForBusinessPlan(this.state.businessPlanStoreAttributes, storeCategory);
      return;
    }

    const q1 = getCurrentQuartil(shoppingCenterData, storeCategory)
      .q1.sort((a, b) => b.year - a.year)[0];
    const q2 = getCurrentQuartil(shoppingCenterData, storeCategory)
      .q2.sort((a, b) => b.year - a.year)[0];
    const q3 = getCurrentQuartil(shoppingCenterData, storeCategory)
      .q3.sort((a, b) => b.year - a.year)[0];
    const q4 = getCurrentQuartil(shoppingCenterData, storeCategory)
      .q4.sort((a, b) => b.year - a.year)[0];
    const q5 = getCurrentQuartil(shoppingCenterData, storeCategory)
      .q5.sort((a, b) => b.year - a.year)[0];
    let kpiSelected = kpiSelectedParam;

    if (kpiSelected !== this.state.currentKpiFilter) {
      this.setState({ currentKpiFilter: kpiSelected });
    }

    const kpiIsDividable = kpiByGla.filter(kpi => kpi === kpiSelected).length > 0;

    this.props.mapviewer.updateColors(getAllDisplayableUnits(visioglobeUnitsMap).map((store) => {
      let storedivider = isCurrencyBySurface && store.kpi &&
      kpiIsDividable && store.kpi.glaWithSst ? store.kpi.glaWithSst : 1;

      if (kpiSelected === salesR12m || kpiSelected === salesR12mWithoutSst) {
        kpiSelected = isCurrencyBySurface ? salesNum : salesR12m;
        storedivider = isCurrencyBySurface && store.kpi &&
        kpiIsDividable && store.kpi.glaDen ? store.kpi.glaDen : 1;
      }

      const storeColor = {
        storeIds: store.idVisioglobes,
        color: visioGlobeDefaultColor,
        storeName: store.storeName || '',
      };

      if (store.kpi) {
        storeColor.storeName = (store.nbSignf && store.nbSignf === 12) ? store.storeName : (`[${store.storeName}]`);
      }

      if (selectedCategory === shop && this.state.selectedStore
        && this.state.selectedStore.idVisioglobes === store.idVisioglobes) {
        storeColor.color = visioGlobeSelectedColor;
        return storeColor;
      }

      if (store.state === reconciliationStates.HISTORY || store.isRental) {
        storeColor.color = visioGlobeUnselectedColor;
        return storeColor;
      }

      const filteredByGlaSplit = storeCategory === all
        || storeCategory === (store.glaSplit || '').toLowerCase();

      if (selectedCategory === branch) {
        const filteredByBranch = selectedBranch.length === 0
          || selectedBranch.filter(branchId => branchId === store.bl1Id).length > 0;
        if (filteredByBranch && filteredByGlaSplit) {
          storeColor.color = branchLevelColor[store.bl1Id];
        }
        return storeColor;
      }

      if (selectedCategory === zone) {
        const filteredByZone = selectedZone.length === 0
          || selectedZone.filter(zoneName => zoneName === store.zoneName).length > 0;
        if (filteredByZone && filteredByGlaSplit) {
          storeColor.color = this.zoneColorMap[store.zoneName];
        }
        return storeColor;
      }

      if (!filteredByGlaSplit) {
        storeColor.color = visioGlobeDefaultColor;
        return storeColor;
      }

      // La répartition pour les quantils de l'ocr est fixe et suit les valeurs suivantes
      if (store && store.kpi && kpiSelectedParam === ocr) {
        const { ocr } = store.kpi;
        switch (true) {
          case ocr === null: storeColor.color = OCR_QUANTIL_COLOR.veryVeryLow; break;
          case ocr < OCR_QUANTIL_VALUE.veryLow: storeColor.color = OCR_QUANTIL_COLOR.veryLow; break;
          case ocr < OCR_QUANTIL_VALUE.low: storeColor.color = OCR_QUANTIL_COLOR.low; break;
          case ocr < OCR_QUANTIL_VALUE.high: storeColor.color = OCR_QUANTIL_COLOR.high; break;
          case ocr < OCR_QUANTIL_VALUE.veryHigh: storeColor.color = OCR_QUANTIL_COLOR.veryHigh; break;
          default: storeColor.color = OCR_QUANTIL_COLOR.veryVeryHigh; break;
        }
        return storeColor;
      }

      let storeKpi = null;
      let q1Kpi = null;
      let q2Kpi = null;
      let q3Kpi = null;
      let q4Kpi = null;
      let q5Kpi = null;
      const quartilDivider = (isCurrencyBySurface && kpiIsDividable ?
        glaDivider(kpiSelected, shoppingCenterData, storeCategory) : 1);

      if (store.kpi) {
        if (kpiSelected === occupancyCost) {
          storeKpi = computeOcupancyCost(store.kpi) / storedivider;
          q1Kpi = computeOcupancyCost(q1) / quartilDivider;
          q2Kpi = computeOcupancyCost(q2) / quartilDivider;
          q3Kpi = computeOcupancyCost(q3) / quartilDivider;
          q4Kpi = computeOcupancyCost(q4) / quartilDivider;
          q5Kpi = computeOcupancyCost(q5) / quartilDivider;
        } else {
          if (store.kpi[kpiSelected]) {
            storeKpi = store.kpi[kpiSelected] / storedivider;
          }
          if (q1 && q1[kpiSelected]) {
            q1Kpi = q1[kpiSelected] / quartilDivider;
          }
          if (q2 && q2[kpiSelected]) {
            q2Kpi = q2[kpiSelected] / quartilDivider;
          }
          if (q3 && q3[kpiSelected]) {
            q3Kpi = q3[kpiSelected] / quartilDivider;
          }
          if (q4 && q4[kpiSelected]) {
            q4Kpi = q4[kpiSelected] / quartilDivider;
          }
          if (q5 && q5[kpiSelected]) {
            q5Kpi = q5[kpiSelected] / quartilDivider;
          }
        }
      }

      if (!storeKpi || storeKpi === 0) {
        storeColor.color = visioGlobeDefaultColor;
      } else if (q5Kpi && storeKpi > q5Kpi) {
        storeColor.color = isKPIValuesReversed(kpiSelected) ?
          geminiColors.veryVeryHigh : geminiColors.veryVeryLow;
      } else if (q4Kpi && storeKpi > q4Kpi) {
        storeColor.color = isKPIValuesReversed(kpiSelected) ?
          geminiColors.veryHigh : geminiColors.veryLow;
      } else if (q3Kpi && storeKpi > q3Kpi) {
        storeColor.color = isKPIValuesReversed(kpiSelected) ? geminiColors.high : geminiColors.low;
      } else if (q2Kpi && storeKpi > q2Kpi) {
        storeColor.color = isKPIValuesReversed(kpiSelected) ? geminiColors.low : geminiColors.high;
      } else if (q1Kpi && storeKpi > q1Kpi) {
        storeColor.color = isKPIValuesReversed(kpiSelected) ?
          geminiColors.veryLow : geminiColors.veryHigh;
      } else {
        storeColor.color = isKPIValuesReversed(kpiSelected) ?
          geminiColors.veryVeryLow : geminiColors.veryVeryHigh;
      }

      return storeColor;
    }));
  };

  selectShoppingCenterCat = () => {
    trackAction(SELECT_POV);
    this.updateMapColor(
      this.props.visioglobeUnitsMap,
      this.props.shoppingCenterData,
      this.state.currentKpiFilter,
      this.state.storeCategory,
      shoppingCenter,
      this.state.selectedBranches,
      this.state.selectedZone,
      this.props.isCurrencyBySurface,
    );
    this.setState({
      selectedCategory: shoppingCenter,
      selectedKpi: this.props.shoppingCenterData.kpi.sort((a, b) => b.year - a.year)[0],
      selectedStore: null,
      selectedZone: [],
      selectedBranches: [],
      categoryOverride: false,
    });
  };

  selectBranchCat = () => {
    trackAction(SELECT_POV);

    this.updateMapColor(
      this.props.visioglobeUnitsMap,
      this.props.shoppingCenterData,
      this.state.currentKpiFilter,
      this.state.storeCategory,
      branch,
      this.state.selectedBranches,
      this.state.selectedZone,
      this.props.isCurrencyBySurface,
    );
    this.setState({
      selectedCategory: branch,
      leftMenuHidden: false,
      selectedKpi: this.sumBlKpis(this.state.selectedBranches)
        || this.props.shoppingCenterData.kpi.sort((a, b) => b.year - a.year)[0],
      selectedStore: null,
      selectedZone: [],
      categoryOverride: false,
    });
  };

  selectStoreCat = () => {
    trackAction(SELECT_POV);
    const storeRes = this.state.selectedStore;
    this.updateMapColor(
      this.props.visioglobeUnitsMap,
      this.props.shoppingCenterData,
      this.state.currentKpiFilter,
      this.state.storeCategory,
      shop,
      this.state.selectedBranches,
      this.state.selectedZone,
      this.props.isCurrencyBySurface,
    );
    this.setState({
      selectedBranches: [],
      selectedZone: [],
      selectedCategory: shop,
      // $FlowFixMe
      leftMenuHidden: false,
      categoryOverride: false,
      // $FlowFixMe
      selectedKpi: storeRes
        ? storeRes.kpi
        : this.props.shoppingCenterData.kpi.sort((a, b) => b.year - a.year)[0],
    });
  };

  selectZoneCat = () => {
    trackAction(SELECT_POV);
    this.updateMapColor(
      this.props.visioglobeUnitsMap,
      this.props.shoppingCenterData,
      this.state.currentKpiFilter,
      this.state.storeCategory,
      zone,
      this.state.selectedBranches,
      this.state.selectedZone,
      this.props.isCurrencyBySurface,
    );
    this.setState({
      leftMenuHidden: false,
      selectedCategory: zone,
      selectedKpi: this.sumZoneKpis(this.state.selectedZone)
        || this.props.shoppingCenterData.kpi.sort((a, b) => b.year - a.year)[0],
      selectedStore: null,
      selectedBranches: [],
      categoryOverride: false,
    });
  };

  updateSelectedBranchLevel = (blId) => {
    if (this.state.selectedCategory === branch) {
      let branchs = this.state.selectedBranches;
      if (branchs.includes(blId)) {
        branchs =
          branchs.filter(elt => elt !== blId);
      } else {
        branchs.push(blId);
      }
      this.updateMapColor(
        this.props.visioglobeUnitsMap,
        this.props.shoppingCenterData,
        this.state.currentKpiFilter,
        this.state.storeCategory,
        this.state.selectedCategory,
        branchs,
        this.state.selectedZone,
        this.props.isCurrencyBySurface,
      );
      this.setState({
        selectedBranches: branchs,
        selectedKpi: this.sumBlKpis(branchs)
          || this.props.shoppingCenterData.kpi.sort((a, b) => b.year - a.year)[0],
        categoryOverride: false,
      });
    }
  };

  updateSelectedZone = (zoneName: string) => {
    if (this.state.selectedCategory === zone) {
      let zones = this.state.selectedZone;
      if (zones.includes(zoneName)) {
        zones =
          zones.filter(elt => elt !== zoneName);
      } else {
        zones.push(zoneName);
      }
      this.updateMapColor(
        this.props.visioglobeUnitsMap,
        this.props.shoppingCenterData,
        this.state.currentKpiFilter,
        this.state.storeCategory,
        this.state.selectedCategory,
        this.state.selectedBranches,
        zones,
        this.props.isCurrencyBySurface,
      );
      this.setState({
        selectedZone: zones,
        selectedKpi: this.sumZoneKpis(zones)
          || this.props.shoppingCenterData.kpi.sort((a, b) => b.year - a.year)[0],
        categoryOverride: false,
      });
    }
  };

  updateSelectedStore = (storeIdVisioglobe, store) => {
    this.props.mapviewer
      // $FlowFixMe
      .selectPlace(storeIdVisioglobe, () => this.forceUpdate());
    this.setState({
      selectedStore: store,
      selectedKpi: this.state.selectedStore ?
        // $FlowFixMe
        this.state.selectedStore.kpi : null,
      // categoryOverride: !this.state.selectedCategory === shop,
    });
  };

  sumBlKpis = (blsId: number[]): KPIs =>
    // $FlowFixMe
    sumKpis(this.props.branchKpi
      .filter(branchKpi => branchKpi.year === this.state.year)
      .filter(branchKpi => blsId
        .filter(selectedBranch => branchKpi.blId === selectedBranch).length > 0));

  sumZoneKpis = (zoneNames: string[]): KPIs =>
    sumKpis(getAllDisplayableUnits(this.props.visioglobeUnitsMap)
      .filter(store => zoneNames
        .filter(selectedZone => store.zoneName === selectedZone).length > 0)
      .map(store => store.kpi));

  selectStoreSize = (storeSize: string) => {
    trackAction(SELECT_US);

    if (this.state.storeCategory !== storeSize.toLowerCase()) {
      this.updateMapColor(
        this.props.visioglobeUnitsMap,
        this.props.shoppingCenterData,
        this.state.currentKpiFilter,
        storeSize.toLowerCase(),
        this.state.selectedCategory,
        this.state.selectedBranches,
        this.state.selectedZone,
        this.props.isCurrencyBySurface,
      );
      this.setState({
        storeCategory: storeSize.toLowerCase(),
        selectedKpi: this.props.shoppingCenterData[storeSize.toLowerCase()]
          .sort((a, b) => b.year - a.year)[0],
      });
    }
  };

  updateSelectedKpiFilter = (kpi: string) => {
    trackAction(KPI_SELECT);
    this.updateMapColor(
      this.props.visioglobeUnitsMap,
      this.props.shoppingCenterData,
      kpi,
      this.state.storeCategory,
      this.state.selectedCategory,
      this.state.selectedBranches,
      this.state.selectedZone,
      this.props.isCurrencyBySurface,
    );
  };

  clearFilter = () => {
    this.setState({
      selectedBranches: [],
      selectedZone: [],
      selectedStore: null,
    }, () => this.updateMapColor(
      this.props.visioglobeUnitsMap,
      this.props.shoppingCenterData,
      this.state.currentKpiFilter,
      this.state.storeCategory,
      this.state.selectedCategory,
      this.state.selectedBranches,
      this.state.selectedZone,
      this.props.isCurrencyBySurface,
    ));
  };

  setShowComparable = (show: boolean) => {
    trackAction(STORE_COMPARABLE);

    this.setState({ showComparable: show });
  };

  setShowChart = (show: boolean) => {
    trackAction(STORE_CHART);

    this.setState({ showChart: show });
  };

  isAllHidden = () => this.state.leftMenuHidden && this.state.rightMenuHidden;

  hideAllViews = () => {
    const isAllHidden = this.isAllHidden();
    this.setState({
      leftMenuHidden: !isAllHidden,
      rightMenuHidden: !isAllHidden,
    });
  };

  getDisplayName = () => {
    let res = this.shoppingCenter ? this.shoppingCenter.name : '';
    if (!this.state.selectedKpi && !this.state.selectedStore) {
      return res;
    }
    switch (this.state.selectedCategory) {
      case shoppingCenter:
        return res;
      case branch:
        if (this.state.categoryOverride) {
          return this.state.selectedStore && this.state.selectedStore.storeName;
        }
        this.state.selectedBranches.forEach((branchId, index) => {
          const result = this.props.branchLevels
            .filter(branchLevel => branchLevel.branchLevel1Id === branchId)[0];
          if (result) {
            res = `${res}  ${result.branchLevel1Label} ${(this.state.selectedBranches.length > 1 && index !== this.state.selectedBranches.length - 1) ? ' - ' : ''}`;
          }
        });
        return res;
      case shop:
        if (this.state.selectedStore) {
          return this.state.selectedStore.storeName;
        }
        return res;
      case zone:
        if (this.state.categoryOverride) {
          return this.state.selectedStore && this.state.selectedStore.storeName;
        }
        this.state.selectedZone.forEach((selectedZone, index) => {
          res = `${res} ${selectedZone} ${(this.state.selectedZone.length > 1 && index !== this.state.selectedZone.length - 1) ? ' - ' : ''}`;
        });
        return res;
      default:
        return res;
    }
  };

  getBranchLevel = (store: ?StoreGemini): string => {
    if (store) {
      const storeRes = store;
      return this.props.branchLevels.filter(branchLevel =>
        branchLevel.branchLevel1Id === storeRes.bl1Id)[0]
        ? this.props.branchLevels
          .filter(branchLevel => branchLevel.branchLevel1Id === storeRes.bl1Id)[0].branchLevel1Label : '';
    }
    return '';
  };

  onSelectBusinessPlan = (businessPlan: ?BusinessPlanBean) => {
    this.setState({
      selectedBusinessPlan: businessPlan,
    });
  };

  changeBusinessPlanAttributes = (attributes: { codstr: number, color: string }[]) => {
    this.setState({ businessPlanStoreAttributes: attributes });
    this.updateColorsForBusinessPlan(attributes, this.state.storeCategory);
  };

  updateColorsForBusinessPlan = (attributes: { codstr: number, color: string }[], storeCategory: string) => {
    this.resetMapToDefault();
    const params = attributes.map((attribute) => {
      const correspondingStore = getAllDisplayableUnits(this.props.visioglobeUnitsMap)
        .filter(store => store.codstr === attribute.codstr)
        .filter(store =>
          storeCategory === all || storeCategory === (store.glaSplit || '').toLowerCase())[0];
      if (!correspondingStore) {
        return {
          storeIds: [],
        };
      }
      const correspondingVisioIds = correspondingStore.idVisioglobes;
      return {
        storeIds: correspondingVisioIds,
        color: attribute.color,
        storeName: correspondingStore.storeName,
        staySelected: correspondingStore === this.state.selectedStore,
      };
    });
    this.props.mapviewer.updateColors(params);
  };

  toggleEmptyUnitMode = () => {
    this.setState(
      {
        isDisplayEmptyUnits: !this.state.isDisplayEmptyUnits,
        displayBusinessPlanModule: false,
      },
      () => {
        if (this.state.isDisplayEmptyUnits) {
          this.updateColorsForEmptyUnits();
        } else {
          this.selectShoppingCenterCat();
        }
      },
    );
  };

  updateColorsForEmptyUnits = () => {
    this.resetMapToDefault();
    const params = this.props.visioglobeUnitsMap.emptyUnit
      .map((emptyUnit: VisioglobeEmptyUnit) => ({
        storeIds: emptyUnit.idVisioglobes,
        color: visioGlobeEmptyUnitColor,
        storeName: emptyUnit.storeName,
      }));
    this.props.mapviewer.updateColors(params);
  };

  getBusinessList = () => {
    if (!this.state.selectedKpi) {
      return [
        {
          label: I18n.t('GEMINI.SALES'),
          subtitle: I18n.t('GEMINI.SALES_SUBTITLE'),
        },
        {
          label: I18n.t('GEMINI.SALES_WITHOUT'),
          subtitle: I18n.t('GEMINI.SALES_WITHOUT_SUBTITLE'),
        },
        {
          label: I18n.t('GEMINI.GLA'),
          subtitle: I18n.t('GEMINI.GLA_SUBTITLE'),
        },
        {
          label: I18n.t('GEMINI.GLA_WITHOUT'),
          subtitle: I18n.t('GEMINI.GLA_WITHOUT_SUBTITLE'),
        },
        {
          label: I18n.t('GEMINI.OCR'),
          subtitle: I18n.t('GEMINI.OCR_SUBTITLE'),
        },
        {
          label: I18n.t('GEMINI.MGR'),
          subtitle: I18n.t('GEMINI.MGR_SUBTITLE'),
        },
        {
          label: I18n.t('GEMINI.ERV'),
          subtitle: I18n.t('GEMINI.ERV_SUBTITLE'),
        },
        {
          label: I18n.t('GEMINI.REV_POT'),
          subtitle: I18n.t('GEMINI.REV_POT_SUBTITLE'),
        },
        {
          label: I18n.t('GEMINI.SBR'),
          subtitle: I18n.t('GEMINI.SBR_SUBTITLE'),
        },
        {
          label: I18n.t('GEMINI.OCC_COST'),
          subtitle: I18n.t('GEMINI.OCC_COST_SUBTITLE'),
        },
      ];
    }
    return [
      {
        label: I18n.t('GEMINI.SALES'),
        subtitle: I18n.t('GEMINI.SALES_SUBTITLE'),
        value: (
          <CurrencySurfaceConvertor
            value={this.props.isCurrencyBySurface
              ? this.state.selectedKpi.salesNum
              : this.state.selectedKpi.salesR12m
            }
            gla={this.state.selectedKpi.glaDen}
            withSymbol
          />
        ),
        onClick: () => this.updateSelectedKpiFilter(salesR12m),
        isSelected: this.state.currentKpiFilter === salesR12m,
        classname: 'sales-kpi-button',
      },
      {
        label: I18n.t('GEMINI.SALES_WITHOUT'),
        subtitle: I18n.t('GEMINI.SALES_WITHOUT_SUBTITLE'),
        value: (
          <CurrencySurfaceConvertor
            value={this.props.isCurrencyBySurface
              ? this.state.selectedKpi.salesNum
              : this.state.selectedKpi.salesR12m
            }
            gla={this.state.selectedKpi.glaDen ? this.state.selectedKpi.glaWithoutSst : 0}
            withSymbol
          />
        ),
        onClick: () => this.updateSelectedKpiFilter(salesR12mWithoutSst),
        isSelected: this.state.currentKpiFilter === salesR12mWithoutSst,
      },
      {
        label: I18n.t('GEMINI.GLA'),
        subtitle: I18n.t('GEMINI.GLA_SUBTITLE'),
        value: (
          <SurfaceConvertor
            value={this.state.selectedKpi.glaWithSst}
          />
        ),
        onClick: () => this.updateSelectedKpiFilter(glaWithSst),
        isSelected: this.state.currentKpiFilter === glaWithSst,
      },
      {
        label: I18n.t('GEMINI.GLA_WITHOUT'),
        subtitle: I18n.t('GEMINI.GLA_WITHOUT_SUBTITLE'),
        value: (
          <SurfaceConvertor
            value={this.state.selectedKpi.glaWithoutSst}
          />
        ),
        onClick: () => this.updateSelectedKpiFilter(glaWithoutSst),
        isSelected: this.state.currentKpiFilter === glaWithoutSst,
      },
      {
        label: I18n.t('GEMINI.OCR'),
        subtitle: I18n.t('GEMINI.OCR_SUBTITLE'),
        value: `${(this.state.selectedKpi.ocr) ? this.state.selectedKpi.ocr.toFixed(1) : 'N/A '} %`,
        onClick: () => this.updateSelectedKpiFilter(ocr),
        isSelected: this.state.currentKpiFilter === ocr,
      },
      {
        label: I18n.t('GEMINI.MGR'),
        subtitle: I18n.t('GEMINI.MGR_SUBTITLE'),
        value: <CurrencySurfaceConvertor
          value={this.state.selectedKpi.spotMgr}
          gla={this.state.selectedKpi.glaWithSst}
          withSymbol
        />,
        onClick: () => this.updateSelectedKpiFilter(spotMgr),
        isSelected: this.state.currentKpiFilter === spotMgr,
      },
      {
        label: I18n.t('GEMINI.ERV'),
        subtitle: I18n.t('GEMINI.ERV_SUBTITLE'),
        value: <CurrencySurfaceConvertor
          value={this.state.selectedKpi.erv}
          gla={this.state.selectedKpi.glaWithSst}
          withSymbol
        />,
        onClick: () => this.updateSelectedKpiFilter(erv),
        isSelected: this.state.currentKpiFilter === erv,
      },
      {
        label: I18n.t('GEMINI.REV_POT'),
        subtitle: I18n.t('GEMINI.REV_POT_SUBTITLE'),
        value: <CurrencySurfaceConvertor
          value={this.state.selectedKpi.revPot}
          gla={this.state.selectedKpi.glaWithSst}
          withSymbol
        />,
        onClick: () => this.updateSelectedKpiFilter(revPot),
        isSelected: this.state.currentKpiFilter === revPot,
      },
      {
        label: I18n.t('GEMINI.SBR'),
        subtitle: I18n.t('GEMINI.SBR_SUBTITLE'),
        value: <CurrencySurfaceConvertor
          value={this.state.selectedKpi.invSbr}
          gla={this.state.selectedKpi.glaWithSst}
          withSymbol
        />,
        onClick: () => this.updateSelectedKpiFilter(invSbr),
        isSelected: this.state.currentKpiFilter === invSbr,
      },
      {
        label: I18n.t('GEMINI.OCC_COST'),
        subtitle: I18n.t('GEMINI.OCC_COST_SUBTITLE'),
        value: <CurrencySurfaceConvertor
          value={computeOcupancyCost(this.state.selectedKpi)}
          gla={this.state.selectedKpi.glaWithSst}
          withSymbol
        />,
        onClick: () => this.updateSelectedKpiFilter(occupancyCost),
        isSelected: this.state.currentKpiFilter === occupancyCost,
      },
    ];
  };

  resetMapToDefault = () => {
    this.props.mapviewer.updateColors(getAllDisplayableUnits(this.props.visioglobeUnitsMap)
      .map(store => ({
        storeIds: store.idVisioglobes,
        color: visioGlobeDefaultColor,
        storeName: store.storeName,
        staySelected: store === this.state.selectedStore,
      })));
  };

  hideLegend = () => this.setState({ legendHidden: !this.state.legendHidden });

  render() {
    const business = this.getBusinessList();
    const showComparable = () => (
      <div className="comparables-modal">
        <ModalWrapper
          showModal={this.setShowComparable}
          title={(<Translate value="SIDE_DATA.COMPARABLE" />)}
          content={
            (
              <ComparablesModalContent
                selectedStore={this.state.selectedStore}
                selectedData={business}
                visioglobeUnitsMap={this.props.visioglobeUnitsMap}
                shoppingCenterName={this.shoppingCenter && this.shoppingCenter.name}
              />
            )
          }
        />
      </div>
    );

    return (
      <Fragment>
        <div className="gemini-web">
          {
            this.state.displayBusinessPlanModule
            && (
              <BusinessPlanTimeline
                changeBusinessPlanAttributes={this.changeBusinessPlanAttributes}
                selectedStore={this.state.selectedStore}
                onSelectBusinessPlan={this.onSelectBusinessPlan}
                selectedBusinessPlanLease={
                  this.state.selectedBusinessPlan
                    ? this.state.selectedBusinessPlan.lease
                    : null
                }
              />
            )
          }
          <GeminiLeftTab
            dispatch={this.props.dispatch}
            isTabCollapsed={this.state.leftMenuHidden}
            collapseTab={() => this.setState({ leftMenuHidden: !this.state.leftMenuHidden })}
            yearMonth={this.state.yearMonth}
            clearFilter={this.clearFilter}
            branchLevels={this.props.branchLevels}
            selectedCategory={this.state.selectedCategory}
            selectedZone={this.state.selectedZone}
            visioglobeUnits={getOkUnits(this.props.visioglobeUnitsMap)}
            updateSelectedBranchLevel={this.updateSelectedBranchLevel}
            updateSelectedZone={this.updateSelectedZone}
            selectedBranches={this.state.selectedBranches}
            updateSelectedStore={this.updateSelectedStore}
            zoneColorMap={this.zoneColorMap}
            selectStoreSize={this.selectStoreSize}
            selectShoppingCenterCat={this.selectShoppingCenterCat}
            selectBranchCat={this.selectBranchCat}
            selectZoneCat={this.selectZoneCat}
            selectStoreCat={this.selectStoreCat}
            storeCategory={this.state.storeCategory}
            isCurrencyBySurface={this.props.isCurrencyBySurface}
            disableActionPOV={this.state.displayBusinessPlanModule || this.state.isDisplayEmptyUnits}
            isPulseAuthorized={this.props.isPulseAuthorized}
          />

          <div className="confidential-message">
            {I18n.t('confidential')}
          </div>

          {
            this.state.showComparable
            && (showComparable())
          }

          {
            this.state.showChart
            && (
              <ModalWrapper
                showModal={this.setShowChart}
                title={I18n.t('GEMINI.GRAPH.CHART')}
                className="gemini-chart-modal"
                content={
                  (
                    <ChartModal
                      dispatch={this.props.dispatch}
                      shoppingCenterData={this.props.shoppingCenterData}
                      codstr={this.state.selectedStore.codstr}
                      retailerId={this.state.selectedStore.retailerId}
                      branchKpiAvg={this.props.branchKpiAvg}
                      yearMonth={this.state.yearMonth}
                    />
                  )
                }
              />
            )
          }
          <GeminiRightTab
            dispatch={this.props.dispatch}
            isTabCollapsed={!this.state.rightMenuHidden}
            collapseTab={() => this.setState({ rightMenuHidden: !this.state.rightMenuHidden })}
            shoppingCenter={this.shoppingCenter}
            history={this.props.history}
            mapviewer={this.props.mapviewer}
            visioglobeUnitsMap={this.props.visioglobeUnitsMap}
            updateVisioglobeUnits={this.props.updateVisioglobeUnits}
            changeMappingMode={this.props.changeMappingMode}
            showComparable={this.setShowComparable}
            showChart={this.setShowChart}
            scrollableElement={this.props.scrollableElement}
            className="side-data-gemini"
            categoryOverride={this.state.categoryOverride}
            selectedCategory={this.state.selectedCategory}
            store={this.state.selectedStore}
            yearMonth={this.state.yearMonth}
            storeBranchLevel={this.getBranchLevel(this.state.selectedStore)}
            title={this.getDisplayName()}
            business={business}
            selectedBusinessPlan={this.state.selectedBusinessPlan}
            onQuitBusinessPlanView={() => this.setState({ selectedBusinessPlan: null })}
            isButton={!this.state.displayBusinessPlanModule && !this.state.isDisplayEmptyUnits}
            isPerf
            isGeminiAdmin={this.props.isGeminiAdmin}
            isPulseAuthorized={this.props.isPulseAuthorized}
          />
          <GeminiLegend
            hideAllViews={this.hideAllViews}
            allHidden={this.isAllHidden()}
            currentKpiFilter={(this.state.currentKpiFilter === salesR12mWithoutSst) ? salesR12m : this.state.currentKpiFilter}
            mapviewer={this.props.mapviewer}
            shoppingCenterData={this.props.shoppingCenterData}
            storeCategory={this.state.storeCategory}
            visioglobeUnits={getOkUnits(this.props.visioglobeUnitsMap)}
            changeMappingMode={() => this.props.changeMappingMode(this.state.selectedStore)}
            isMappingUser={this.props.isMappingUser}
            displayBusinessPlan={() => {
              this.setState({
                displayBusinessPlanModule: !this.state.displayBusinessPlanModule,
                selectedBusinessPlan: null,
                selectedStore: null,
                isDisplayEmptyUnits: false,
              }, () => {
                if (!this.state.displayBusinessPlanModule) {
                  this.resetMapToDefault();
                  this.selectShoppingCenterCat();
                }
              });
            }}
            isBusinessPlanModuleDisplayed={this.state.displayBusinessPlanModule}
            isQuartilsLegendHidden={
              this.state.selectedCategory === branch
              || this.state.selectedCategory === zone
              || this.state.displayBusinessPlanModule
              || this.state.isDisplayEmptyUnits
            }
            displayEmptyUnits={this.toggleEmptyUnitMode}
            isDisplayEmptyUnits={this.state.isDisplayEmptyUnits}
          />
        </div>
        <div className="mobile-legend-container">
          <div className="mobile-legend">
            <IconButton
              icon={`${!this.state.legendHidden ? 'fas fa-times' : 'fas fa-ellipsis-h'}`}
              className="expand-button legend-button"
              onClick={() => this.hideLegend()}
            />
            <QuantilsLegend
              visioglobeUnits={getOkUnits(this.props.visioglobeUnitsMap)}
              currentKpiFilter={(this.state.currentKpiFilter === salesR12mWithoutSst) ? salesR12m : this.state.currentKpiFilter}
              storeCategory={this.state.storeCategory}
              shoppingCenterData={this.props.shoppingCenterData}
              isCurrencyBySurface={this.props.isCurrencyBySurface}
              className={`mobile-quantils ${this.state.legendHidden ? 'hidden' : ''}`}
            />
          </div>
          <VisioGlobeUI
            className={`${this.state.legendHidden ? 'hidden' : ''}`}
            mapviewer={this.props.mapviewer}
          />
        </div>
        <div
          className={`gemini-mobile ${!this.state.isFilters ? 'to-right' : ''} ${this.state.showChart || this.state.showComparable ? 'noscroll' : ''}`}
        >
          <GeminiLeftTab
            id="left-filters"
            dispatch={this.props.dispatch}
            isTabCollapsed={this.state.leftMenuHidden}
            collapseTab={() => this.setState({ leftMenuHidden: !this.state.leftMenuHidden })}
            yearMonth={this.state.yearMonth}
            clearFilter={this.clearFilter}
            branchLevels={this.props.branchLevels}
            selectedCategory={this.state.selectedCategory}
            selectedZone={this.state.selectedZone}
            visioglobeUnits={getOkUnits(this.props.visioglobeUnitsMap)}
            updateSelectedBranchLevel={this.updateSelectedBranchLevel}
            updateSelectedZone={this.updateSelectedZone}
            selectedBranches={this.state.selectedBranches}
            updateSelectedStore={this.updateSelectedStore}
            zoneColorMap={this.zoneColorMap}
            selectStoreSize={this.selectStoreSize}
            selectShoppingCenterCat={this.selectShoppingCenterCat}
            selectBranchCat={this.selectBranchCat}
            selectZoneCat={this.selectZoneCat}
            selectStoreCat={this.selectStoreCat}
            storeCategory={this.state.storeCategory}
            isCurrencyBySurface={this.props.isCurrencyBySurface}
            isUp={this.state.isUp}
            disableActionPOV={this.state.displayBusinessPlanModule || this.state.isDisplayEmptyUnits}
            isPulseAuthorized={this.props.isPulseAuthorized}
          />
          <button
            className={`swipe-button ${this.state.isUp ? 'isUp' : ''}`}
            onClick={() => this.setState({ isFilters: !this.state.isFilters })}
          >
            {
              this.state.isFilters
                ? (
                  <span><Translate value="SIDE_DATA.analysis" /></span>
                ) : (
                  <span className="rotate"><Translate value="MARKETING.FILTERS" /></span>
                )}
            <i className={`fas ${this.state.isFilters ? 'fa-chevron-right' : 'fa-chevron-left'}`} />
          </button>
          {
            this.state.showComparable
            && (showComparable())
          }

          {
            this.state.showChart
            && (
              <ModalWrapper
                showModal={this.setShowChart}
                title={I18n.t('GEMINI.GRAPH.CHART')}
                className="gemini-chart-modal"
                content={
                  (
                    <ChartModal
                      dispatch={this.props.dispatch}
                      shoppingCenterData={this.props.shoppingCenterData}
                      codstr={this.state.selectedStore.codstr}
                      retailerId={this.state.selectedStore.retailerId}
                      branchKpiAvg={this.props.branchKpiAvg}
                      yearMonth={this.state.yearMonth}
                    />
                  )
                }
              />
            )
          }
          <GeminiRightTab
            id="right-data"
            dispatch={this.props.dispatch}
            isTabCollapsed={!this.state.rightMenuHidden}
            collapseTab={() => this.setState({ rightMenuHidden: !this.state.rightMenuHidden })}
            shoppingCenter={this.shoppingCenter}
            history={this.props.history}
            mapviewer={this.props.mapviewer}
            visioglobeUnitsMap={this.props.visioglobeUnitsMap}
            updateVisioglobeUnits={this.props.updateVisioglobeUnits}
            changeMappingMode={this.props.changeMappingMode}
            showComparable={this.setShowComparable}
            showChart={this.setShowChart}
            scrollableElement={this.props.scrollableElement}
            className={`side-data-gemini ${this.state.isUp ? 'isUp' : ''}`}
            categoryOverride={this.state.categoryOverride}
            selectedCategory={this.state.selectedCategory}
            store={this.state.selectedStore}
            yearMonth={this.state.yearMonth}
            storeBranchLevel={this.getBranchLevel(this.state.selectedStore)}
            title={this.getDisplayName()}
            business={business}
            selectedBusinessPlan={this.state.selectedBusinessPlan}
            onQuitBusinessPlanView={() => this.setState({ selectedBusinessPlan: null })}
            isButton={!this.state.displayBusinessPlanModule && !this.state.isDisplayEmptyUnits}
            isPerf
            isGeminiAdmin={this.props.isGeminiAdmin}
            isPulseAuthorized={this.props.isPulseAuthorized}
          />
        </div>
      </Fragment>
    );
  }
}

export default withRouter(connect(state => ({
  branchLevels: state.shoppingCenterFilters.branchLevelList,
  isCurrencyBySurface: state.store.isCurrencyBySurface,
}))(Overlay));
